import * as SliderPrimitive from '@radix-ui/react-slider';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export function Button({ className, variant = 'primary', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'quiet' | 'outline' | 'danger';
}) {
  return (
    <button
      className={cn('button', `button-${variant}`, className)}
      {...props}
    />
  );
}

export function Panel({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return <section className={cn('panel', className)} {...props} />;
}

export function SectionHeading({ title, detail, children }: { title: string; detail?: string; children?: ReactNode }) {
  return (
    <div className="section-heading">
      <div>
        <h2>{title}</h2>
        {detail ? <p>{detail}</p> : null}
      </div>
      {children}
    </div>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'accent' | 'warning' }) {
  return <span className={cn('badge', `badge-${tone}`)}>{children}</span>;
}

export function RangeSlider({ value, onValueChange, onValueCommit, min, max, step = 1, label, valueText, centered = false }: {
  value: number;
  onValueChange: (value: number) => void;
  onValueCommit?: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  label: string;
  valueText?: string;
  centered?: boolean;
}) {
  const percentage = (value - min) / (max - min) * 100;
  return (
    <SliderPrimitive.Root
      className="range-slider"
      value={[value]}
      onValueChange={(next) => {
        const nextValue = next[0];
        if (nextValue !== undefined) onValueChange(nextValue);
      }}
      onValueCommit={(next) => {
        const nextValue = next[0];
        if (nextValue !== undefined) onValueCommit?.(nextValue);
      }}
      min={min}
      max={max}
      step={step}
      aria-label={label}
    >
      <SliderPrimitive.Track className="range-track">
        {centered ? <>
          <span className="range-fill" style={{ left: `${Math.min(50, percentage)}%`, width: `${Math.abs(percentage - 50)}%` }} aria-hidden="true" />
          <span className="range-center" aria-hidden="true" />
        </> : <SliderPrimitive.Range className="range-fill" />}
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb className="range-thumb" aria-label={label} aria-valuetext={valueText} />
    </SliderPrimitive.Root>
  );
}
